/**
 * A real camera, inside the app.
 *
 * WHY THIS EXISTS, AFTER TWO FAILED ATTEMPTS AT THE SIMPLE WAY
 *
 * The obvious approach is `<input type="file" accept="image/*" capture>`, and
 * that is what the app did. It is correct HTML, and twice it was reported as
 * opening the photo gallery instead of the camera. The second time I
 * "fixed" it by widening `accept` to `image/*`, which IS required for Chrome
 * on Android to honour `capture` — and the gallery still opened.
 *
 * The reason is that `capture` is a HINT, not an instruction. An installed PWA
 * and an Android WebView both routinely ignore it and show the system picker.
 * No amount of attribute tuning changes that, which is why the third attempt
 * stops trying.
 *
 * `getUserMedia` is not a hint. It opens the camera, or it tells you why it
 * could not. The driver sees a live preview and a shutter button, which is
 * what was asked for in plain words — "give live camera option".
 *
 * WHAT THIS BUYS BEYOND OBEDIENCE
 *
 *   * A delivery photo is taken AT the door. The gallery let a driver attach
 *     a picture from last week, which is evidence of nothing.
 *   * The selfie can face the right way. `facingMode: 'user'` for attendance,
 *     'environment' for goods — the file input could not choose.
 *   * No 12-megapixel round trip. The frame is grabbed at preview resolution
 *     and compressed, so a shop-door upload over 4G is fast.
 *
 * THE FALLBACK IS NOT OPTIONAL. A denied permission, an older browser, or a
 * device with no camera all end up at the file input — because a driver who
 * cannot photograph anything cannot complete a delivery, and refusing to let
 * him try is worse than a picture from the gallery.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from './ui';
import { compress, pickImage, type CompressedImage, type CompressOptions } from '../lib/camera';

export interface CameraSheetProps {
  open: boolean;
  /** 'environment' photographs the world; 'user' is a selfie. */
  facing?: 'environment' | 'user';
  title?: string;
  onCapture: (image: CompressedImage) => void;
  onClose: () => void;
  compressOptions?: CompressOptions;
}

export function CameraSheet({
  open, facing = 'environment', title, onCapture, onClose, compressOptions,
}: CameraSheetProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [ready, setReady] = useState(false);

  /**
   * Stopping the tracks is not tidiness — it is the camera light.
   *
   * A stream left running keeps the indicator on after the sheet closes, and
   * on a field phone that reads as the app watching you. It also holds the
   * camera against the next screen that wants it.
   */
  const stop = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setReady(false);
  }, []);

  useEffect(() => {
    if (!open) { stop(); return undefined; }

    let cancelled = false;
    setError(null);

    (async () => {
      if (!navigator.mediaDevices?.getUserMedia) {
        setError('This phone will not open the camera in the app.');
        return;
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          // `ideal`, not `exact`: a tablet with only one camera should still
          // work rather than failing because it has no rear lens.
          video: { facingMode: { ideal: facing }, width: { ideal: 1600 } },
          audio: false,
        });
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => { /* autoplay policy */ });
        }
        setReady(true);
      } catch (err) {
        const name = (err as { name?: string })?.name ?? '';
        setError(
          name === 'NotAllowedError'
            ? 'Camera permission was refused. Allow it in your browser settings, or choose a photo instead.'
            : name === 'NotFoundError'
              ? 'No camera found on this device.'
              : 'The camera would not start.',
        );
      }
    })();

    return () => { cancelled = true; stop(); };
  }, [open, facing, stop]);

  const shoot = async () => {
    const video = videoRef.current;
    if (!video || !ready) return;
    setBusy(true);
    try {
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('no 2d context');
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      const blob: Blob | null = await new Promise((res) => {
        canvas.toBlob((b) => res(b), 'image/jpeg', 0.92);
      });
      if (!blob) throw new Error('could not read the frame');

      // Through the same compressor every other photo uses, so the size caps
      // and the stored dimensions match what the server already expects.
      onCapture(await compress(blob, compressOptions));
      stop();
      onClose();
    } catch {
      setError('Could not take the photo. Try again, or choose a file.');
    } finally {
      setBusy(false);
    }
  };

  /** The escape hatch. See the note at the top: never leave them stuck. */
  const chooseFile = async () => {
    const [file] = await pickImage({});
    if (!file) return;
    setBusy(true);
    try {
      onCapture(await compress(file, compressOptions));
      stop();
      onClose();
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title ?? 'Camera'}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000, background: '#000',
        display: 'flex', flexDirection: 'column',
      }}
    >
      <div style={{
        padding: '12px 16px', color: '#fff', display: 'flex',
        justifyContent: 'space-between', alignItems: 'center',
      }}
      >
        <span className="t-semi">{title ?? 'Take a photo'}</span>
        <Button size="sm" variant="ghost" onClick={() => { stop(); onClose(); }}>Close</Button>
      </div>

      <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
        <video
          ref={videoRef}
          playsInline
          muted
          style={{
            width: '100%', height: '100%', objectFit: 'cover',
            // A selfie that is not mirrored looks wrong to the person taking
            // it — they move left and the image moves right.
            transform: facing === 'user' ? 'scaleX(-1)' : undefined,
          }}
        />
        {error && (
          <div style={{
            position: 'absolute', inset: 0, display: 'grid', placeItems: 'center',
            padding: 24, textAlign: 'center', color: '#fff',
          }}
          >
            <div className="stack-3">
              <p className="t-sm">{error}</p>
              <Button variant="quiet" onClick={() => void chooseFile()}>Choose a photo instead</Button>
            </div>
          </div>
        )}
      </div>

      <div style={{ padding: 20, display: 'grid', placeItems: 'center', gap: 12 }}>
        <button
          type="button"
          aria-label="Take the photo"
          disabled={!ready || busy}
          onClick={() => void shoot()}
          style={{
            width: 72, height: 72, borderRadius: '50%',
            border: '4px solid #fff', background: busy ? '#888' : '#fff',
            opacity: ready ? 1 : 0.4,
          }}
        />
        {!error && !ready && <span className="t-xs" style={{ color: '#fff' }}>Starting the camera…</span>}
      </div>
    </div>
  );
}
